import { ArrowDown, AlertTriangle, MousePointerClick, Plug, Target } from 'lucide-react';
import { Card, CardHeader, CardBody, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { cn } from '@/lib/utils';
import type { CommandCenter, ContentPageRow } from '@/services/marketingIntegrations.service';
import { formatNumber } from './derive';

interface Props {
  cc: CommandCenter;
}

interface FunnelStep {
  label: string;
  sessions: number;
  description: string;
}

interface CroFix {
  page: string;
  fix: string;
  estRevenueImpactMonthly: number;
  rationale: string;
}

function buildFunnel(pages: ContentPageRow[]): FunnelStep[] {
  if (!pages.length) return [];
  const total = pages.reduce((s, p) => s + p.sessions, 0);
  const engagedTotal = pages.reduce((s, p) => s + Math.round(p.sessions * (p.engagementRate / 100)), 0);
  const productSessions = pages.filter((p) => p.category === 'product' || p.category === 'pricing').reduce((s, p) => s + p.sessions, 0);
  const conversions = pages.reduce((s, p) => s + p.conversions, 0);

  return [
    { label: 'Site sessions', sessions: total, description: 'All landed sessions in window' },
    { label: 'Engaged', sessions: engagedTotal, description: 'Engaged session events from GA4' },
    { label: 'Product / pricing visits', sessions: productSessions, description: 'Reached commercial pages' },
    { label: 'Conversions', sessions: Math.round(conversions), description: 'Tracked GA4 conversion events' },
  ];
}

function dropOff(prev: number, current: number): number {
  if (prev === 0) return 0;
  return ((prev - current) / prev) * 100;
}

function hypothesisFor(stepLabel: string, drop: number): string {
  if (stepLabel === 'Engaged') {
    return `${drop.toFixed(0)}% of visitors bounce or leave fast — page-load speed, weak headline, or wrong-traffic-on-wrong-page mismatch.`;
  }
  if (stepLabel === 'Product / pricing visits') {
    return `${drop.toFixed(0)}% never reach the commercial pages — internal CTAs are too weak or buried below the fold.`;
  }
  if (stepLabel === 'Conversions') {
    return `${drop.toFixed(0)}% of commercial visitors don't convert — friction on the form, slow follow-up, or trust signals missing.`;
  }
  return `${drop.toFixed(0)}% drop. Investigate.`;
}

function fixFor(stepLabel: string): string {
  if (stepLabel === 'Engaged') return 'Run PageSpeed Insights, rewrite the H1 to match top inbound query, gate by source.';
  if (stepLabel === 'Product / pricing visits') return 'Add sticky/inline CTA blocks in long-form content. Test "see pricing" CTA copy.';
  if (stepLabel === 'Conversions') return 'Reduce form fields to 3, add testimonials above the fold, enable enhanced conversions for accurate signal.';
  return 'Audit and re-test.';
}

function buildCroFixes(pages: ContentPageRow[]): CroFix[] {
  const list: CroFix[] = [];
  // 1. Highest-traffic broken funnel
  const broken = [...pages]
    .filter((p) => p.sessions >= 500 && p.conversions === 0)
    .sort((a, b) => b.sessions - a.sessions)[0];
  if (broken) {
    const estLeads = Math.round(broken.sessions * 0.025);
    list.push({
      page: broken.path,
      fix: 'Add primary CTA + lead capture form + GA4 conversion event',
      estRevenueImpactMonthly: estLeads * 200,
      rationale: `${formatNumber(broken.sessions)} sessions, 0 conversions. At 2.5% baseline that's ~${estLeads} leads/mo.`,
    });
  }

  // 2. Hidden-gem highest engagement
  const gem = [...pages]
    .filter((p) => p.engagementRate >= 80 && p.conversions === 0 && p.sessions >= 100)
    .sort((a, b) => b.engagementRate - a.engagementRate)[0];
  if (gem) {
    const estLeads = Math.round(gem.sessions * 0.035);
    list.push({
      page: gem.path,
      fix: 'Add scroll-triggered email capture + retargeting pixel',
      estRevenueImpactMonthly: estLeads * 200,
      rationale: `${gem.engagementRate.toFixed(0)}% engagement on ${formatNumber(gem.sessions)} sessions — these readers are pre-qualified.`,
    });
  }

  // 3. Pricing page conversion lift
  const pricing = pages.filter((p) => p.category === 'pricing').sort((a, b) => b.sessions - a.sessions)[0];
  if (pricing) {
    const conv = pricing.conversions;
    const sessions = pricing.sessions;
    const baseline = sessions > 0 ? (conv / sessions) * 100 : 0;
    const targetRate = Math.max(5, baseline + 2);
    const liftLeads = Math.round(sessions * ((targetRate - baseline) / 100));
    list.push({
      page: pricing.path,
      fix: 'Add testimonials, FAQ, money-back signal above the fold',
      estRevenueImpactMonthly: liftLeads * 250,
      rationale: `Current rate ${baseline.toFixed(2)}%, conservative target ${targetRate.toFixed(1)}% → ~${liftLeads} extra leads/mo.`,
    });
  }

  // 4. Long-form blog → CTA path
  const longBlog = [...pages]
    .filter((p) => (p.category === 'blog' || p.category === 'guide') && p.avgDuration >= 60 && p.sessions >= 200)
    .sort((a, b) => b.sessions - a.sessions)[0];
  if (longBlog) {
    const estLeads = Math.round(longBlog.sessions * 0.015);
    list.push({
      page: longBlog.path,
      fix: 'Inline CTA at 50% scroll + content upgrade gate',
      estRevenueImpactMonthly: estLeads * 200,
      rationale: `${(longBlog.avgDuration).toFixed(0)}s avg duration → reader is already in deep. CTA placement is the only missing piece.`,
    });
  }

  return list.sort((a, b) => b.estRevenueImpactMonthly - a.estRevenueImpactMonthly);
}

export default function FunnelTab({ cc }: Props) {
  if (!cc.contentPages?.ok) {
    return (
      <div className="p-6">
        <EmptyState
          icon={<Plug className="h-8 w-8 text-slate-400" />}
          title="GA4 not connected"
          description={cc.contentPages?.error ?? 'Connect Google Analytics 4 to build the live session funnel.'}
          action={{ label: 'Connect GA4', onClick: () => { window.location.href = '/admin/marketing-integrations'; } }}
        />
      </div>
    );
  }

  const pages = cc.contentPages.rows ?? [];
  const funnel = buildFunnel(pages);
  const croFixes = buildCroFixes(pages);

  if (funnel.length === 0) {
    return (
      <div className="p-6">
        <EmptyState
          icon={<Target className="h-8 w-8 text-slate-400" />}
          title="No page data yet"
          description="GA4 is connected but hasn't reported sessions in the lookback window."
        />
      </div>
    );
  }

  // Pre-compute drops
  const drops = funnel.map((step, i) => i === 0 ? 0 : dropOff(funnel[i - 1].sessions, step.sessions));

  return (
    <div className="space-y-6 p-6">
      {/* Visual funnel */}
      <Card>
        <CardHeader><CardTitle>Session funnel</CardTitle></CardHeader>
        <CardBody>
          <div className="space-y-3">
            {funnel.map((step, i) => {
              const widthPct = funnel[0].sessions > 0 ? (step.sessions / funnel[0].sessions) * 100 : 0;
              const drop = drops[i];
              const isHighDrop = drop >= 30;
              return (
                <div key={step.label}>
                  <div className="flex justify-between items-baseline mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900">{step.label}</span>
                      <span className="text-xs text-slate-500">{step.description}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold tabular-nums text-slate-900">{formatNumber(step.sessions)}</div>
                      {i > 0 && (
                        <div className={cn('text-xs tabular-nums', isHighDrop ? 'text-red-600 font-semibold' : 'text-slate-500')}>
                          ▼ {drop.toFixed(1)}%
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="h-7 bg-slate-100 rounded relative overflow-hidden">
                    <div
                      className={cn('h-full rounded transition-all', isHighDrop ? 'bg-red-200' : 'bg-blue-200')}
                      style={{ width: `${Math.max(widthPct, 0.5)}%` }}
                    />
                  </div>
                  {i < funnel.length - 1 && (
                    <div className="flex justify-center my-1.5"><ArrowDown className="h-4 w-4 text-slate-400" /></div>
                  )}
                </div>
              );
            })}
          </div>
        </CardBody>
      </Card>

      {/* Hypotheses for big drops */}
      {drops.some((d) => d >= 30) && (
        <section>
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Drop-off hypotheses (≥30%)</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {funnel.map((step, i) => {
              if (i === 0 || drops[i] < 30) return null;
              return (
                <div key={`hyp-${step.label}`} className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <Badge variant="warning" size="sm">{drops[i].toFixed(0)}% drop</Badge>
                  </div>
                  <div className="text-sm font-semibold text-slate-900 mb-2">{step.label}</div>
                  <div className="text-xs text-slate-700 mb-3 leading-relaxed">{hypothesisFor(step.label, drops[i])}</div>
                  <div className="text-xs">
                    <span className="font-semibold text-slate-900">Fix:</span>{' '}
                    <span className="text-slate-700">{fixFor(step.label)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Landing page audit */}
      <Card>
        <CardHeader><CardTitle>Landing page audit · top 20 by sessions</CardTitle></CardHeader>
        <CardBody className="-mx-4 -mb-4 sm:-mx-5 sm:-mb-5">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2.5 text-left font-semibold">Page</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Sessions</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Engagement</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Avg duration</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Conversions</th>
                  <th className="px-3 py-2.5 text-right font-semibold">CVR</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pages.slice(0, 20).map((p) => {
                  const cvr = p.sessions > 0 ? (p.conversions / p.sessions) * 100 : 0;
                  return (
                    <tr key={p.path} className="hover:bg-slate-50">
                      <td className="px-4 py-2.5 max-w-[300px]">
                        <a href={p.path} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-mono text-xs" title={p.path}>
                          {p.path.length > 50 ? p.path.slice(0, 47) + '…' : p.path}
                        </a>
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{formatNumber(p.sessions)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{p.engagementRate.toFixed(1)}%</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{p.avgDuration.toFixed(0)}s</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{formatNumber(Math.round(p.conversions))}</td>
                      <td className={cn('px-3 py-2.5 text-right tabular-nums font-semibold', cvr === 0 ? 'text-red-600' : cvr >= 3 ? 'text-emerald-600' : 'text-slate-700')}>
                        {cvr.toFixed(2)}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      {/* CRO fixes ranked */}
      {croFixes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MousePointerClick className="h-4 w-4 text-emerald-600" />
              CRO fixes ranked by est. monthly revenue impact
            </CardTitle>
          </CardHeader>
          <CardBody>
            <ol className="space-y-3">
              {croFixes.map((fix, i) => (
                <li key={fix.page} className="flex gap-4 items-start py-3 border-b border-slate-100 last:border-0">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center font-bold text-sm">{i + 1}</div>
                  <div className="flex-1">
                    <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1">
                      <a href={fix.page} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-mono text-xs">
                        {fix.page}
                      </a>
                      <span className="text-emerald-700 font-bold tabular-nums">~${formatNumber(fix.estRevenueImpactMonthly)} / mo</span>
                    </div>
                    <div className="text-sm font-semibold text-slate-900">{fix.fix}</div>
                    <div className="text-xs text-slate-600 mt-1">{fix.rationale}</div>
                  </div>
                </li>
              ))}
            </ol>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
